import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import { useToast } from '../ui/ToastProvider'
import { formatApiDateTimeDisplay, formatMenuInstant } from '../lib/date'
import MenuGuideButton from '../components/MenuGuideButton'
import { PdfTemplateMapper } from './Customers'
import { useAuth } from '../auth/AuthContext'
import { buildAfterContractTemplatePreviewValues } from '../lib/claimTemplatePreviewSamples'
import { PDF_MAPPING_FONT_OPTIONS } from '../lib/pdfMappingFontOptions'

const AFTER_CONTRACT_FORMS = [
  {
    code: 'FA_NOTICE',
    name: '금소법_FA고지의무확인서',
    description: '금융소비자보호법 계약체결 고지의무 확인서',
  },
  {
    code: 'FULL_SALES',
    name: '완전판매확인서',
    description: '완전판매 및 상품설명 내용에 대한 계약자·피보험자 확인서',
  },
  {
    code: 'CREDIT_INFO_CONSENT',
    name: '개인신용정보동의서',
    description: '계약 후 제출용 개인신용정보 처리 동의서',
  },
]

const FIELD_LABELS = {
  applicationNumber: '청약번호',
  policyNumber: '증권번호',
  designNumber: '설계번호',
  insuranceCompanyName: '보험회사명',
  insurerName: '보험회사명(보험사)',
  productName: '상품명',
  contractorName: '계약자 성명',
  contractorBirthDate: '계약자 생년월일',
  contractorPhone: '계약자 연락처',
  contractorSignature: '계약자 서명',
  contractorHandwriting_understood: '계약자 자필: 설명을 듣고 이해',
  contractorHandwriting_disclosure: '계약자 자필: 성실히 고지',
  contractorHandwriting_explanation: '설계사 작성: 설명(계약자 서식)',
  contractorHandwriting_confirm: '설계사 작성: 확인(계약자 서식)',
  insuredName: '피보험자 성명',
  insuredBirthDate: '피보험자 생년월일',
  insuredPhone: '피보험자 연락처',
  insuredSignature: '피보험자 서명',
  insuredHandwriting_understood: '피보험자 자필: 설명을 듣고 이해',
  insuredHandwriting_disclosure: '피보험자 자필: 성실히 고지',
  insuredHandwriting_explanation: '설계사 작성: 설명(피보험자 서식)',
  insuredHandwriting_confirm: '설계사 작성: 확인(피보험자 서식)',
  plannerName: '모집자 성명',
  plannerOrgName: '모집자 소속',
  plannerPhone: '모집자 연락처',
  plannerSignature: '모집자 서명 이미지',
  plannerNameHandwriting: '모집자 성명 자필 이미지',
  plannerHandwriting_explanation: '모집자 자필: 설명',
  plannerHandwriting_confirm: '모집자 자필: 확인',
  customerHandwriting_understood: '고객 자필: 설명을 듣고 이해',
  customerHandwriting_disclosure: '고객 자필: 성실히 고지',
  customerHandwriting_explanation: '설계사 작성: 설명',
  customerHandwriting_confirm: '설계사 작성: 확인',
  printedAt: '출력일',
  printedAtYear: '출력일(년)',
  printedAtYear2: '출력일(년 2자리)',
  printedAtMonth: '출력일(월)',
  printedAtDay: '출력일(일)',
  documentDate: '확인서 기재일',
  documentDateYear: '확인서 기재일(년)',
  documentDateYear2: '확인서 기재일(년 2자리)',
  documentDateMonth: '확인서 기재일(월)',
  documentDateDay: '확인서 기재일(일)',
  contractDate: '계약일',
  contractDateYear: '계약일(년)',
  contractDateMonth: '계약일(월)',
  contractDateDay: '계약일(일)',
  defaultVCheck: 'V 체크',
  defaultStaticText: '고정 문구',
}

const FIELD_SECTIONS = [
  {
    title: '고객 입력·수령 필드',
    fields: [
      'policyNumber',
      'designNumber',
      'insuranceCompanyName',
      'productName',
      'contractorName',
      'contractorSignature',
      'contractorHandwriting_understood',
      'insuredName',
      'insuredSignature',
      'insuredHandwriting_disclosure',
    ],
  },
  {
    title: '모집자 자동 입력',
    fields: [
      'plannerName',
      'plannerSignature',
      'plannerHandwriting_explanation',
      'plannerHandwriting_confirm',
    ],
  },
  {
    title: '출력일·기본값',
    fields: [
      'printedAt',
      'printedAtYear',
      'printedAtYear2',
      'printedAtMonth',
      'printedAtDay',
      'documentDate',
      'documentDateYear',
      'documentDateYear2',
      'documentDateMonth',
      'documentDateDay',
      'defaultVCheck',
      'defaultStaticText',
    ],
  },
]

function formatTemplateUpdatedAt(record) {
  if (record && typeof record === 'object') return formatMenuInstant(record, 'updatedAt')
  return formatApiDateTimeDisplay(undefined, record)
}

function safeFilenamePart(s, maxLen = 48) {
  const x = String(s ?? '')
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/\s+/g, '_')
    .trim()
  if (!x) return 'after_contract_certificate'
  return x.length > maxLen ? x.slice(0, maxLen) : x
}

function parseFieldsJson(raw) {
  if (!raw) return []
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function nextFieldKey(fields) {
  const existingKeys = (fields || []).map((f) => f.key).filter((k) => k && String(k).startsWith('key_'))
  const maxNum = existingKeys.reduce((max, k) => {
    const match = String(k).match(/^key_(\d+)$/)
    return match ? Math.max(max, parseInt(match[1], 10)) : max
  }, 0)
  return `key_${maxNum + 1}`
}

function inferFieldType(bindKey) {
  const key = String(bindKey || '')
  if (key.includes('Signature')) return 'image'
  if (key.includes('Handwriting_understood') || key.includes('Handwriting_disclosure')) return 'image'
  if (key === 'plannerHandwriting_explanation' || key === 'plannerHandwriting_confirm') return 'image'
  return 'text'
}

export default function AfterContractCertificateTemplatesPage() {
  const { show } = useToast()
  const { user } = useAuth()
  const [selectedFormCode, setSelectedFormCode] = useState('FA_NOTICE')
  const [statusByCode, setStatusByCode] = useState({})
  const [templateFileId, setTemplateFileId] = useState('')
  const [formVersion, setFormVersion] = useState('')
  const [fields, setFields] = useState([])
  const [selectedFieldKey, setSelectedFieldKey] = useState(null)
  const [pdfPageIndex, setPdfPageIndex] = useState(0)
  const [uploadingTemplate, setUploadingTemplate] = useState(false)
  const [saving, setSaving] = useState(false)
  const [mappingBusy, setMappingBusy] = useState(false)
  const [fieldEditorOpen, setFieldEditorOpen] = useState(false)
  const [expandedSections, setExpandedSections] = useState({})
  const [resettingFormCode, setResettingFormCode] = useState(null)
  const [mappingExportIncludeFileId, setMappingExportIncludeFileId] = useState(false)
  const [orgWhitelistRows, setOrgWhitelistRows] = useState([])
  const [orgWhitelistDraft, setOrgWhitelistDraft] = useState('')
  const [whitelistBusy, setWhitelistBusy] = useState(false)
  const [orgSearchQuery, setOrgSearchQuery] = useState('')
  const [orgSearchResults, setOrgSearchResults] = useState([])
  const [orgSearchBusy, setOrgSearchBusy] = useState(false)
  const [pdfCoordinatePreviewEnabled, setPdfCoordinatePreviewEnabled] = useState(true)

  const afterContractPreviewValues = useMemo(() => buildAfterContractTemplatePreviewValues(user), [user])

  const selectedForm = useMemo(
    () => AFTER_CONTRACT_FORMS.find((f) => f.code === selectedFormCode) || AFTER_CONTRACT_FORMS[0],
    [selectedFormCode],
  )

  const loadStatuses = useCallback(async () => {
    try {
      const params = new URLSearchParams()
      AFTER_CONTRACT_FORMS.forEach((f) => params.append('formCodes', f.code))
      const res = await api.get(`/ops/form-templates/after-contract-certificates/statuses?${params.toString()}`)
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      const rows = Array.isArray(payload) ? payload : []
      const next = {}
      for (const row of rows) {
        if (!row?.formCode) continue
        next[row.formCode] = row
      }
      setStatusByCode(next)
    } catch {
      setStatusByCode({})
    }
  }, [])

  const loadOrgWhitelist = useCallback(async () => {
    try {
      const res = await api.get('/ops/form-templates/after-contract-certificates/org-whitelist')
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      const rows = Array.isArray(payload) ? payload : []
      setOrgWhitelistRows(rows)
      setOrgWhitelistDraft(rows.map((r) => String(r.orgId ?? '')).filter(Boolean).join('\n'))
    } catch {
      setOrgWhitelistRows([])
      setOrgWhitelistDraft('')
    }
  }, [])

  const loadActiveTemplate = useCallback(async () => {
    if (!selectedFormCode) return
    try {
      const res = await api.get('/ops/form-templates/after-contract-certificates/active', {
        params: { formCode: selectedFormCode },
      })
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      setTemplateFileId(payload?.templateFileId || '')
      setFormVersion(payload?.formVersion || '')
      setFields(parseFieldsJson(payload?.fieldsJson))
      setSelectedFieldKey(null)
      setPdfPageIndex(0)
    } catch (err) {
      if (err?.response?.status !== 404) {
        console.warn('Failed to load after-contract template:', err)
      }
      setTemplateFileId('')
      setFormVersion('')
      setFields([])
      setSelectedFieldKey(null)
      setPdfPageIndex(0)
    }
  }, [selectedFormCode])

  useEffect(() => {
    loadStatuses()
  }, [loadStatuses])

  useEffect(() => {
    loadOrgWhitelist()
  }, [loadOrgWhitelist])

  useEffect(() => {
    loadActiveTemplate()
  }, [loadActiveTemplate])

  const parseWhitelistDraftIds = (text) => {
    const raw = String(text || '')
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => Number(s))
      .filter((n) => Number.isFinite(n) && n > 0)
    return [...new Set(raw)]
  }

  const addOrgIdToWhitelistDraft = (orgId) => {
    const id = Number(orgId)
    if (!Number.isFinite(id) || id <= 0) return
    const existing = new Set(parseWhitelistDraftIds(orgWhitelistDraft))
    if (existing.has(id)) {
      show('이미 허용 목록 초안에 포함된 조직 ID입니다.', 'info')
      return
    }
    existing.add(id)
    const sorted = [...existing].sort((a, b) => a - b)
    setOrgWhitelistDraft(sorted.join('\n'))
    show(`조직 ID ${id}을(를) 목록에 추가했습니다. 저장 시 반영됩니다.`, 'success')
  }

  const runOrgSearch = async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault()
    const q = String(orgSearchQuery || '').trim()
    if (!q) {
      setOrgSearchResults([])
      show('조직명 또는 조직 ID를 입력해 주세요.', 'info')
      return
    }
    setOrgSearchBusy(true)
    try {
      const res = await api.get('/ops/form-templates/after-contract-certificates/org-search', {
        params: { q },
      })
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      const rows = Array.isArray(payload) ? payload : []
      setOrgSearchResults(rows)
      if (!rows.length) {
        show('일치하는 조직이 없습니다.', 'info')
      }
    } catch {
      setOrgSearchResults([])
      show('조직 검색에 실패했습니다.', 'error')
    } finally {
      setOrgSearchBusy(false)
    }
  }

  const saveOrgWhitelist = async () => {
    const ids = parseWhitelistDraftIds(orgWhitelistDraft)
    setWhitelistBusy(true)
    try {
      const res = await api.put('/ops/form-templates/after-contract-certificates/org-whitelist', { orgIds: ids })
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      const rows = Array.isArray(payload) ? payload : []
      setOrgWhitelistRows(rows)
      setOrgWhitelistDraft(rows.map((r) => String(r.orgId ?? '')).filter(Boolean).join('\n'))
      show('허용 조직 목록을 저장했습니다.', 'success')
    } catch {
      show('허용 조직 저장에 실패했습니다.', 'error')
    } finally {
      setWhitelistBusy(false)
    }
  }

  const handleTemplateUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.type !== 'application/pdf') {
      show('PDF 파일만 업로드 가능합니다.', 'error')
      return
    }
    setUploadingTemplate(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const res = await api.post('/files', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      const fileId = payload?.fileId || payload?.id
      if (fileId) {
        setTemplateFileId(fileId)
        show('템플릿 PDF가 업로드되었습니다.', 'success')
      }
    } catch {
      show('템플릿 PDF 업로드에 실패했습니다.', 'error')
    } finally {
      setUploadingTemplate(false)
      e.target.value = ''
    }
  }

  const downloadTemplateFile = async () => {
    if (!templateFileId) return
    try {
      const res = await api.get(`/files/${templateFileId}/download`, { responseType: 'blob' })
      const blob = new Blob([res.data], { type: res?.headers?.['content-type'] || 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const cd = res?.headers?.['content-disposition'] || res?.headers?.['Content-Disposition'] || ''
      const filenameMatch = cd.match(/filename\*=UTF-8''([^;]+)/i) || cd.match(/filename="([^"]+)"/i)
      a.download = filenameMatch ? decodeURIComponent(filenameMatch[1]) : `${selectedFormCode}_${templateFileId}.pdf`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch {
      show('템플릿 다운로드에 실패했습니다.', 'error')
    }
  }

  const addField = (bindKey, overrides = {}) => {
    if (!bindKey) return
    const uniqueKey = nextFieldKey(fields)
    const fieldType = overrides.type || inferFieldType(bindKey)
    const isCheck = overrides.valueType === 'CHECK_V'
    const next = {
      key: uniqueKey,
      bindKey,
      pageIndex: pdfPageIndex,
      x: 0.1,
      y: 0.1,
      w: fieldType === 'image' ? 0.16 : (isCheck ? 0.035 : 0.2),
      h: fieldType === 'image' ? 0.055 : (isCheck ? 0.028 : 0.03),
      fontSize: isCheck ? 11 : 12,
      align: isCheck ? 'center' : 'left',
      type: fieldType,
      valueType: '',
      value: '',
      format: '',
      fontKey: '',
      ...overrides,
    }
    setFields([...fields, next])
    setSelectedFieldKey(uniqueKey)
  }

  const addQuickVCheckField = ({ pageIndex, x, y }) => {
    const w = 0.035
    const h = 0.028
    const uniqueKey = nextFieldKey(fields)
    const next = {
      key: uniqueKey,
      bindKey: 'defaultVCheck',
      pageIndex: Math.max(0, Number(pageIndex ?? pdfPageIndex ?? 0)),
      x: Math.max(0, Math.min(1 - w, Number(x ?? 0) - w / 2)),
      y: Math.max(0, Math.min(1 - h, Number(y ?? 0) - h / 2)),
      w,
      h,
      fontSize: 11,
      align: 'center',
      type: 'text',
      valueType: 'CHECK_V',
      value: '',
      format: '',
      fontKey: '',
    }
    setFields([...fields, next])
    setSelectedFieldKey(uniqueKey)
    show('우클릭 위치에 V 체크 필드를 추가했습니다.', 'success')
  }

  const updateField = (key, updates) => {
    setFields(fields.map((f) => (f.key === key ? { ...f, ...updates } : f)))
  }

  const updateFields = (updates) => {
    const patchByKey = new Map((updates || []).map((item) => [item.key, item.box || {}]))
    setFields((prev) => prev.map((f) => (patchByKey.has(f.key) ? { ...f, ...patchByKey.get(f.key) } : f)))
  }

  const handleSave = async () => {
    if (!selectedFormCode) {
      show('서식을 선택해주세요.', 'error')
      return
    }
    if (!templateFileId) {
      show('템플릿 PDF를 업로드해주세요.', 'error')
      return
    }
    setSaving(true)
    try {
      await api.post('/ops/form-templates/after-contract-certificates', {
        formCode: selectedFormCode,
        formVersion: formVersion || null,
        templateFileId,
        fields,
      })
      show('3종확인서 템플릿이 저장되었습니다.', 'success')
      await loadStatuses()
      await loadActiveTemplate()
    } catch (e) {
      show('템플릿 저장에 실패했습니다.', 'error')
    } finally {
      setSaving(false)
    }
  }

  const resetCoordinatesForForm = async (formCode) => {
    const ok = window.confirm('이 서식의 필드 좌표를 모두 비웁니다. PDF 파일은 유지됩니다. 계속할까요?')
    if (!ok) return
    setResettingFormCode(formCode)
    try {
      await api.post('/ops/form-templates/after-contract-certificates/mappings/reset-fields', null, {
        params: { formCode },
      })
      show('좌표를 초기화했습니다.', 'success')
      if (selectedFormCode === formCode) {
        setFields([])
        setSelectedFieldKey(null)
      }
      await loadStatuses()
    } catch (err) {
      if (err?.response?.status === 404) {
        show('활성 템플릿을 찾을 수 없습니다.', 'error')
      } else {
        show('좌표 초기화에 실패했습니다.', 'error')
      }
    } finally {
      setResettingFormCode(null)
    }
  }

  const downloadSelectedMapping = async () => {
    setMappingBusy(true)
    try {
      const res = await api.get('/ops/form-templates/after-contract-certificates/mappings/export', {
        params: { formCodes: [selectedFormCode] },
      })
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      const cloned = JSON.parse(JSON.stringify(payload || {}))
      const items = Array.isArray(cloned.items) ? cloned.items : []
      if (!items.length) {
        show('선택한 서식에 활성 템플릿이 없습니다.', 'error')
        return
      }
      if (!mappingExportIncludeFileId) {
        for (const row of items) {
          if (row && typeof row === 'object') delete row.templateFileId
        }
      }
      const json = JSON.stringify(cloned, null, 2)
      const blob = new Blob([json], { type: 'application/json' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `after_contract_mapping_${safeFilenamePart(selectedForm?.name || selectedFormCode)}_${new Date().toISOString().slice(0, 10)}.json`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
      show('좌표 JSON을 내려받았습니다.', 'success')
    } catch {
      show('좌표 JSON 다운로드에 실패했습니다.', 'error')
    } finally {
      setMappingBusy(false)
    }
  }

  const importMappingsJson = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setMappingBusy(true)
    try {
      const text = await file.text()
      const parsed = JSON.parse(text)
      let items = []
      if (Array.isArray(parsed?.items)) {
        items = parsed.items.map((row) => ({ ...row }))
      } else if (Array.isArray(parsed?.fields)) {
        items = [{
          formCode: parsed.formCode || selectedFormCode,
          formVersion: parsed.formVersion || null,
          templateFileId: parsed.templateFileId || templateFileId || null,
          fields: parsed.fields,
        }]
      } else if (Array.isArray(parsed)) {
        items = parsed.map((row) => ({ ...row }))
      }
      if (!items.length) {
        show('가져올 좌표 항목이 없습니다.', 'error')
        return
      }
      const payloadItems = items.map((row) => ({
        formCode: row.formCode || selectedFormCode,
        formVersion: row.formVersion ?? null,
        templateFileId: row.templateFileId ?? null,
        fields: row.fields,
      }))
      const res = await api.post('/ops/form-templates/after-contract-certificates/mappings/import', { items: payloadItems })
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      if ((payload?.failed ?? 0) > 0) {
        const errors = (payload?.results || [])
          .filter((r) => !r.ok)
          .map((r) => r.error || '알 수 없는 오류')
          .join(', ')
        show(`좌표 가져오기 실패: ${errors}`, 'error')
      } else {
        show(`좌표 가져오기 완료: ${payload?.success ?? 0}개 성공`, 'success')
      }
      await loadStatuses()
      await loadActiveTemplate()
    } catch (err) {
      show(`좌표 JSON 가져오기에 실패했습니다: ${err?.message || '알 수 없는 오류'}`, 'error')
    } finally {
      setMappingBusy(false)
      e.target.value = ''
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold">3종확인서(인카)</h1>
            <MenuGuideButton slug="template-management" title="템플릿관리 가이드" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 text-sm">
          <Link className="rounded border px-3 py-1.5 hover:bg-slate-50" to="/ops/forms/claims/templates">청구서식</Link>
          <Link className="rounded border px-3 py-1.5 hover:bg-slate-50" to="/ops/templates/messages">메시지</Link>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-bold text-slate-900">3종확인서 작성 허용 조직 (화이트리스트)</h2>
            <p className="mt-1 text-xs leading-relaxed text-slate-500">
              조직 ID(<span className="font-mono">orgs.id</span>)를 한 줄에 하나씩 또는 쉼표로 구분해 입력하거나, 아래에서 조직명으로 검색해 추가할 수 있습니다. 상위 조직만 등록해도 그 하위 조직(<span className="font-mono">parent_org_id</span> 트리) 소속 사용자는 자동으로 허용됩니다. 목록이 비어 있으면 GA 사용자는 누구도 작성 화면을 쓸 수 없습니다. 운영자는 예외로 항상 접근 가능합니다.
            </p>
          </div>
          <button
            type="button"
            disabled={whitelistBusy}
            onClick={() => saveOrgWhitelist()}
            className="shrink-0 rounded bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
          >
            {whitelistBusy ? '저장 중…' : '허용 목록 저장'}
          </button>
        </div>
        <form className="mt-3 flex flex-wrap items-end gap-2" onSubmit={runOrgSearch}>
          <div className="min-w-[200px] flex-1">
            <label htmlFor="after-contract-org-search" className="block text-xs font-medium text-slate-700">
              조직명·ID 검색
            </label>
            <input
              id="after-contract-org-search"
              type="text"
              value={orgSearchQuery}
              onChange={(e) => setOrgSearchQuery(e.target.value)}
              placeholder="예: 인카 / 서울본부 / 12"
              className="mt-1 w-full rounded border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-sgez-primary focus:ring-1 focus:ring-sgez-primary"
              autoComplete="off"
            />
          </div>
          <button
            type="submit"
            disabled={orgSearchBusy}
            className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {orgSearchBusy ? '검색 중…' : '검색'}
          </button>
        </form>
        {orgSearchResults.length > 0 && (
          <div className="mt-2 max-h-52 overflow-y-auto rounded border border-slate-200 bg-white">
            <table className="min-w-full text-left text-xs">
              <thead className="sticky top-0 bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-3 py-2 font-semibold">조직 ID</th>
                  <th className="px-3 py-2 font-semibold">조직명</th>
                  <th className="px-3 py-2 font-semibold">유형</th>
                  <th className="px-3 py-2 font-semibold text-right">추가</th>
                </tr>
              </thead>
              <tbody>
                {orgSearchResults.map((row) => (
                  <tr key={row.orgId} className="border-t border-slate-100">
                    <td className="px-3 py-1.5 font-mono text-slate-800">{row.orgId}</td>
                    <td className="px-3 py-1.5 text-slate-800">{row.orgName || '-'}</td>
                    <td className="px-3 py-1.5 text-slate-600">{row.orgType || '-'}</td>
                    <td className="px-3 py-1.5 text-right">
                      <button
                        type="button"
                        onClick={() => addOrgIdToWhitelistDraft(row.orgId)}
                        className="rounded border border-teal-600 bg-teal-50 px-2 py-1 text-[11px] font-semibold text-teal-900 hover:bg-teal-100"
                      >
                        목록에 넣기
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <textarea
          className="mt-3 w-full rounded border border-slate-200 bg-slate-50/50 px-3 py-2 font-mono text-xs text-slate-800 outline-none focus:border-sgez-primary focus:ring-1 focus:ring-sgez-primary"
          rows={4}
          value={orgWhitelistDraft}
          onChange={(e) => setOrgWhitelistDraft(e.target.value)}
          placeholder="예: 12&#10;34"
        />
        {orgWhitelistRows.length > 0 && (
          <div className="mt-3 overflow-x-auto rounded border border-slate-100">
            <table className="min-w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-3 py-2 font-semibold">조직 ID</th>
                  <th className="px-3 py-2 font-semibold">조직명</th>
                </tr>
              </thead>
              <tbody>
                {orgWhitelistRows.map((row) => (
                  <tr key={row.orgId} className="border-t border-slate-100">
                    <td className="px-3 py-1.5 font-mono text-slate-800">{row.orgId}</td>
                    <td className="px-3 py-1.5 text-slate-700">{row.orgName || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
        <aside className="space-y-3">
          <div className="rounded border bg-white p-3">
            <div className="mb-2 text-sm font-semibold text-slate-900">서식 선택</div>
            <div className="space-y-2">
              {AFTER_CONTRACT_FORMS.map((form) => {
                const st = statusByCode[form.code]
                const selected = selectedFormCode === form.code
                return (
                  <button
                    key={form.code}
                    type="button"
                    className={`w-full rounded border p-3 text-left transition ${
                      selected ? 'border-slate-900 bg-slate-900 text-white' : 'bg-white hover:bg-slate-50'
                    }`}
                    onClick={() => setSelectedFormCode(form.code)}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold">{form.name}</span>
                      <span className={`shrink-0 rounded px-1.5 py-0.5 text-[11px] ${selected ? 'bg-white/15 text-white' : st?.hasTemplate ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                        {st?.hasTemplate ? '등록' : '미등록'}
                      </span>
                    </div>
                    <div className={`mt-1 text-xs ${selected ? 'text-white/70' : 'text-slate-500'}`}>
                      {st?.hasTemplate ? `${st.formVersion || '버전 미입력'} · ${formatTemplateUpdatedAt(st)}` : form.description}
                    </div>
                  </button>
                )
              })}
            </div>
          </div>

          <div className="rounded border bg-white p-3">
            <div className="text-sm font-semibold text-slate-900">좌표 작업</div>
            <p className="mt-1 text-xs leading-relaxed text-slate-500">
              필드를 추가한 뒤 PDF 위에서 드래그하면 해당 필드의 좌표가 저장됩니다. V 체크는 PDF 위에서 우클릭하면 바로 생성됩니다.
            </p>
            <div className="mt-3 flex flex-col gap-2">
              <button
                type="button"
                className="rounded border border-emerald-600 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-900 hover:bg-emerald-100"
                onClick={() => addField('defaultVCheck', { valueType: 'CHECK_V', type: 'text', align: 'center' })}
              >
                V 체크 추가
              </button>
              <button
                type="button"
                className="rounded border px-3 py-2 text-sm hover:bg-slate-50"
                onClick={() => addField('defaultStaticText', { valueType: 'STATIC', value: '설명', type: 'text' })}
              >
                고정 문구 추가
              </button>
              <button
                type="button"
                className="rounded border border-rose-200 px-3 py-2 text-sm text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                disabled={!statusByCode[selectedFormCode]?.hasTemplate || resettingFormCode != null}
                onClick={() => resetCoordinatesForForm(selectedFormCode)}
              >
                {resettingFormCode === selectedFormCode ? '초기화 중...' : '좌표 초기화'}
              </button>
            </div>
          </div>
        </aside>

        <section className="space-y-4 min-w-0">
          <div className="rounded border bg-white p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-xl font-bold">{selectedForm.name}</h2>
                <p className="mt-1 text-sm text-slate-600">{selectedForm.description}</p>
              </div>
              <div className="flex flex-col items-stretch gap-2 sm:items-end">
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="rounded border px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-60"
                    onClick={downloadSelectedMapping}
                    disabled={mappingBusy || !statusByCode[selectedFormCode]?.hasTemplate}
                  >
                    좌표 JSON 다운로드
                  </button>
                  <label className={`rounded border px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer ${mappingBusy ? 'opacity-60 pointer-events-none' : ''}`}>
                    좌표 JSON 업로드
                    <input type="file" accept="application/json" className="hidden" onChange={importMappingsJson} />
                  </label>
                </div>
                <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={mappingExportIncludeFileId}
                    onChange={(e) => setMappingExportIncludeFileId(e.target.checked)}
                  />
                  <span>다운로드에 templateFileId 포함</span>
                </label>
              </div>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
              <div>
                <label className="mb-1 block text-sm text-slate-600">양식 버전 (선택)</label>
                <input
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={formVersion}
                  onChange={(e) => setFormVersion(e.target.value)}
                  placeholder="예: 2024.05"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm text-slate-600">템플릿 PDF *</label>
                <input
                  type="file"
                  accept="application/pdf"
                  className="w-full rounded border px-3 py-2 text-sm"
                  onChange={handleTemplateUpload}
                  disabled={uploadingTemplate}
                />
                {templateFileId && (
                  <div className="mt-2 text-xs text-slate-600">
                    업로드됨: {templateFileId}
                    <button
                      type="button"
                      className="ml-2 text-blue-600 hover:underline"
                      onClick={() => window.open(`/files/${templateFileId}/preview`, '_blank')}
                    >
                      미리보기
                    </button>
                    <button
                      type="button"
                      className="ml-2 text-blue-600 hover:underline"
                      onClick={downloadTemplateFile}
                    >
                      다운로드
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                className="rounded bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
                onClick={handleSave}
                disabled={saving || !templateFileId}
              >
                {saving ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>

          {templateFileId ? (
            <>
              <div className="rounded border bg-white p-3">
                <div className="mb-3 text-sm font-semibold">필드 선택</div>
                <div className="space-y-2">
                  {FIELD_SECTIONS.map((section) => {
                    const expanded = expandedSections[section.title] !== false
                    return (
                      <div key={section.title} className="border-b border-slate-200 pb-2 last:border-b-0 last:pb-0">
                        <button
                          type="button"
                          className="mb-2 flex w-full items-center justify-between text-xs font-semibold text-slate-700 hover:text-slate-900"
                          onClick={() => setExpandedSections((prev) => ({ ...prev, [section.title]: !expanded }))}
                        >
                          <span>{section.title}</span>
                          <span className="text-slate-400">{expanded ? '▼' : '▶'}</span>
                        </button>
                        {expanded && (
                          <div className="flex flex-wrap gap-2">
                            {section.fields.map((key) => (
                              <button
                                key={key}
                                type="button"
                                className="max-w-[220px] rounded border bg-white px-3 py-1 text-sm hover:bg-slate-50"
                                onClick={() => addField(key)}
                                title={`${FIELD_LABELS[key] || key} (${key})`}
                              >
                                <span className="block truncate">{FIELD_LABELS[key] || key}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
                <div className="mt-3 text-xs text-slate-500">
                  현재 선택: <span className="font-medium">
                    {selectedFieldKey
                      ? (() => {
                          const f = fields.find((x) => x.key === selectedFieldKey)
                          return f ? `${FIELD_LABELS[f.bindKey] || f.bindKey} (${f.key})` : '필드를 선택하세요'
                        })()
                      : '필드를 선택하세요'}
                  </span>
                </div>
              </div>

              <div className="rounded border bg-white p-3">
                <div className="mb-2 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      className="rounded border-slate-300"
                      checked={pdfCoordinatePreviewEnabled}
                      onChange={(e) => setPdfCoordinatePreviewEnabled(e.target.checked)}
                    />
                    <span>좌표에 예상 입력값 미리보기</span>
                  </label>
                  <span className="text-xs text-slate-500">샘플·로그인 계정 기준이며 PDF와 다를 수 있습니다.</span>
                </div>
                <PdfTemplateMapper
                  fileId={templateFileId}
                  fields={fields}
                  selectedKey={selectedFieldKey}
                  onSelectKey={setSelectedFieldKey}
                  onSetBox={(key, box) => updateField(key, { ...box })}
                  onSetBoxes={updateFields}
                  onQuickCheckAdd={addQuickVCheckField}
                  enableBulkTools
                  previewEnabled={pdfCoordinatePreviewEnabled}
                  previewValues={afterContractPreviewValues}
                  onPageIndexChange={(idx) => {
                    if (typeof idx === 'number' && !Number.isNaN(idx)) {
                      setPdfPageIndex(Math.max(0, idx))
                    }
                  }}
                  fieldLabels={FIELD_LABELS}
                  onDeleteField={(key) => {
                    setFields(fields.filter((f) => f.key !== key))
                    if (selectedFieldKey === key) setSelectedFieldKey(null)
                  }}
                />
              </div>

              <div className="rounded border bg-slate-50 p-3">
                <button
                  type="button"
                  className="flex w-full items-center justify-between rounded px-1 py-1.5 text-left text-sm font-medium text-slate-800 hover:bg-slate-100"
                  onClick={() => setFieldEditorOpen((v) => !v)}
                  aria-expanded={fieldEditorOpen}
                >
                  <span>필드 편집 ({fields.length}개)</span>
                  <span className="text-xs text-slate-400">{fieldEditorOpen ? '▼ 접기' : '▶ 펼치기'}</span>
                </button>
                {fieldEditorOpen && (
                  <div className="mt-2 overflow-x-auto">
                    <table className="min-w-full text-xs">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-2 py-1 text-left">key</th>
                          <th className="px-2 py-1 text-left">bindKey</th>
                          <th className="px-2 py-1 text-left">type</th>
                          <th className="px-2 py-1 text-left">page</th>
                          <th className="px-2 py-1 text-left">x</th>
                          <th className="px-2 py-1 text-left">y</th>
                          <th className="px-2 py-1 text-left">w</th>
                          <th className="px-2 py-1 text-left">h</th>
                          <th className="px-2 py-1 text-left">font</th>
                          <th className="px-2 py-1 text-left" title="서버에 해당 글꼴 파일이 있을 때 PDF에 반영">PDF 폰트</th>
                          <th className="px-2 py-1 text-left">align</th>
                          <th className="px-2 py-1 text-left">valueType</th>
                          <th className="px-2 py-1 text-left">value</th>
                          <th className="px-2 py-1"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {fields.map((f, idx) => (
                          <tr key={f.key || idx} className="border-t">
                            <td className="px-2 py-1 text-slate-500">{f.key}</td>
                            <td className="px-2 py-1">
                              <input
                                className="w-48 rounded border px-2 py-1"
                                value={f.bindKey || ''}
                                onChange={(e) => updateField(f.key, { bindKey: e.target.value })}
                              />
                            </td>
                            <td className="px-2 py-1">
                              <select
                                className="w-24 rounded border px-2 py-1"
                                value={f.type || 'text'}
                                onChange={(e) => updateField(f.key, { type: e.target.value })}
                              >
                                <option value="text">text</option>
                                <option value="image">image</option>
                                <option value="checkbox">checkbox</option>
                              </select>
                            </td>
                            <td className="px-2 py-1">
                              <input className="w-14 rounded border px-2 py-1" type="number" value={f.pageIndex ?? 0} onChange={(e) => updateField(f.key, { pageIndex: Number(e.target.value) })} />
                            </td>
                            {['x', 'y', 'w', 'h'].map((k) => (
                              <td key={k} className="px-2 py-1">
                                <input className="w-16 rounded border px-2 py-1" type="number" step="0.001" value={f[k] ?? 0} onChange={(e) => updateField(f.key, { [k]: Number(e.target.value) })} />
                              </td>
                            ))}
                            <td className="px-2 py-1">
                              <input className="w-14 rounded border px-2 py-1" type="number" value={f.fontSize ?? 12} onChange={(e) => updateField(f.key, { fontSize: Number(e.target.value) })} />
                            </td>
                            <td className="px-2 py-1">
                              <select
                                className="max-w-[9rem] rounded border px-1 py-1 text-[11px]"
                                value={f.fontKey || ''}
                                onChange={(e) => updateField(f.key, { fontKey: e.target.value })}
                              >
                                {PDF_MAPPING_FONT_OPTIONS.map((opt) => (
                                  <option key={opt.value || 'auto'} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="px-2 py-1">
                              <select className="w-20 rounded border px-2 py-1" value={f.align || 'left'} onChange={(e) => updateField(f.key, { align: e.target.value })}>
                                <option value="left">left</option>
                                <option value="center">center</option>
                                <option value="right">right</option>
                              </select>
                            </td>
                            <td className="px-2 py-1">
                              <select className="w-28 rounded border px-2 py-1" value={f.valueType || ''} onChange={(e) => updateField(f.key, { valueType: e.target.value })}>
                                <option value="">-</option>
                                <option value="CHECK_V">CHECK_V</option>
                                <option value="STATIC">STATIC</option>
                                <option value="TODAY_YYYY">TODAY_YYYY</option>
                                <option value="TODAY_MM">TODAY_MM</option>
                                <option value="TODAY_DD">TODAY_DD</option>
                              </select>
                            </td>
                            <td className="px-2 py-1">
                              <input className="w-32 rounded border px-2 py-1" value={f.value || ''} onChange={(e) => updateField(f.key, { value: e.target.value })} />
                            </td>
                            <td className="px-2 py-1">
                              <button
                                type="button"
                                className="rounded border border-rose-200 px-2 py-1 text-rose-700 hover:bg-rose-50"
                                onClick={() => {
                                  setFields(fields.filter((x) => x.key !== f.key))
                                  if (selectedFieldKey === f.key) setSelectedFieldKey(null)
                                }}
                              >
                                삭제
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="rounded border border-dashed bg-slate-50 p-8 text-center text-sm text-slate-600">
              먼저 선택한 서식의 PDF를 업로드해 주세요.
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
